import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import Box from '@mui/material/Box';
import { SxProps } from '@mui/system/styleFunctionSx';
import { ErrorScreen } from 'components/screens/parts/ErrorScreen';
import { Text } from 'components/Text';
import { getTrans } from 'modules/i18n';
import { Layout } from 'modules/minimal-ui-theme';
import { GuestFormState } from './GuestFormSessionContext';

/** Every outcome that is neither loading nor "get on with the form". */
export type GuestFormStatus = Exclude<GuestFormState, 'loading' | 'active'>;

const successStyles: SxProps = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  minHeight: '80vh',
  gap: 3
};

/**
 * The end of the road, whichever road it was.
 *
 * `submitted` is a success and gets a success treatment — no button, because a guest has nowhere
 * to go from here. Everything else is a genuine dead end and keeps the error treatment, but each
 * state has its own words: "the form was closed" and "this link never worked" ask different things
 * of the reader, and one generic apology would leave them guessing which applies.
 *
 * The CV success animation is deliberately not reused: it says "your CV was submitted", which a
 * form is not.
 */
export const GuestFormStatusScreen = ({
  state
}: {
  state: GuestFormStatus;
}) => {
  // No cast: TypeScript expands these template literals into the union of concrete keys and checks
  // each against the locale file, so a state without copy fails the build rather than rendering an
  // empty screen.
  const title = getTrans(`guestForm.status.${state}.title`);
  const description1 = getTrans(`guestForm.status.${state}.body1`);
  const description2 = getTrans(`guestForm.status.${state}.body2`);

  if (state === 'submitted') {
    return (
      <Layout childrenStyles={successStyles}>
        <CheckCircleOutlineIcon
          color="primary"
          sx={{ fontSize: 72 }}
          aria-hidden="true"
        />
        <Box
          sx={{
            maxWidth: 460,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1
          }}
        >
          <Text variant="h4" color="text.primary">
            {title}
          </Text>
          <Text>{description1}</Text>
          <Text>{description2}</Text>
        </Box>
      </Layout>
    );
  }

  return (
    <ErrorScreen
      title={title}
      description1={description1}
      description2={description2}
    />
  );
};
